'use client';

import { translateInline } from '@/globals/translate/translateInline';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import {
	ComponentPropsWithoutRef,
	KeyboardEvent,
	ReactNode,
	Ref,
	createContext,
	use,
	useId,
	useRef,
	useState,
} from 'react';

const CurrentViewButton = ({ children }: { children: ReactNode }) => (
	<span
		className={classes(
			box({
				paddingBlock: '-1 | small',
				paddingInline: '1 | h3',
				borderWidth: '3px',
				backgroundColor: 'negative',
			}),
			textBlock({ fontWeight: 'bold' }),
		)}
	>
		{children}
	</span>
);

type SwitchActiveViewButtonProps = {
	children: ReactNode;
	isActive?: boolean;
	buttonRef?: Ref<HTMLButtonElement>;
} & Omit<ComponentPropsWithoutRef<'button'>, 'children' | 'ref'>;

const SwitchActiveViewButton = ({
	children,
	isActive,
	buttonRef,
	...props
}: SwitchActiveViewButtonProps) => (
	<button
		ref={buttonRef}
		{...props}
		className={classes(
			box({
				paddingBlock: isActive ? '0px' : '-1 | small',
				paddingInline: isActive ? '0px' : '1 | h3',
			}),
			textBlock(
				isActive
					? {}
					: { color: 'positiveBlue', textDecorationLine: 'underline' },
			),
		)}
	>
		{children}
	</button>
);

type View = 'graph' | 'table';

const CbsViewContext = createContext<View | undefined>(undefined);

export const CbbsGraphViewSwitcher = ({
	children,
}: {
	children: ReactNode;
}) => {
	const id = useId();
	const [activeView, setActiveView] = useState<View>('graph');
	const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
	const graphLabel = translateInline({ en: 'Graph', nl: 'Grafiek' });
	const tableLabel = translateInline({ en: 'Table', nl: 'Tabel' });
	const tabListLabel = translateInline({
		en: 'Select graph or table view',
		nl: 'Selecteer grafiek- of tabelweergave',
	});
	const tabs: Array<{ view: View; label: string; id: string }> = [
		{ view: 'graph', label: graphLabel, id: `cbs-view-tab-graph-${id}` },
		{ view: 'table', label: tableLabel, id: `cbs-view-tab-table-${id}` },
	];

	const activeTabIndex = tabs.findIndex((tab) => tab.view === activeView);

	const focusTabAtIndex = (index: number) => {
		const total = tabs.length;
		const normalizedIndex = ((index % total) + total) % total;
		tabRefs.current[normalizedIndex]?.focus();
	};

	const onTabKeyDown = (
		event: KeyboardEvent<HTMLButtonElement>,
		index: number,
	) => {
		switch (event.key) {
			case 'ArrowRight': {
				event.preventDefault();
				focusTabAtIndex(index + 1);
				return;
			}
			case 'ArrowLeft': {
				event.preventDefault();
				focusTabAtIndex(index - 1);
				return;
			}
		}
	};

	return (
		<>
			<div
				role="tablist"
				aria-label={tabListLabel}
				className={classes(
					shelf({}),
					textStyle({ capSize: '-1 | small' }),
					box({
						backgroundColor: 'negativeBlue',
						borderWidth: '1px',
						paddingInline: 'negative 1 | h3',
					}),
				)}
			>
				{tabs.map(({ view, label, id }, index) => {
					const isActive = activeView === view;

					return (
						<SwitchActiveViewButton
							key={view}
							id={id}
							role="tab"
							aria-selected={isActive}
							aria-controls={`cbs-view-panel-${id}`}
							tabIndex={isActive ? 0 : -1}
							onClick={() => {
								setActiveView(view);
							}}
							onFocus={() => {
								setActiveView(view);
							}}
							onKeyDown={(event) => {
								onTabKeyDown(event, index);
							}}
							isActive={isActive}
							buttonRef={(node: HTMLButtonElement | null) => {
								tabRefs.current[index] = node;
								return () => {};
							}}
						>
							{isActive ? (
								<CurrentViewButton>{label}</CurrentViewButton>
							) : (
								label
							)}
						</SwitchActiveViewButton>
					);
				})}
			</div>
			<CbsViewContext value={activeView}>
				<div
					id={`cbs-view-panel-${id}`}
					role="tabpanel"
					aria-labelledby={tabs[activeTabIndex]?.id}
					tabIndex={0}
				>
					{children}
				</div>
			</CbsViewContext>
		</>
	);
};

export const useCbsActiveView = () => {
	const context = use(CbsViewContext);
	if (context === undefined) {
		throw new Error('useCbsView must be used within a CbsGraphProvider');
	}
	return context;
};
